// @flow
// noinspection ExceptionCaughtLocallyJS

import {LIValidationError} from "./LIValidationError";
import type SampleBasic from "./SampleBasic";
import {CaseBreakdownError} from "./CaseBreakdownError";

export const PostProcessingRuleTypes = {
    NONE: -1,
    TEXT_MATCH: 0,
    GREATER_THAN_MATCH: 1,
    LESS_THAN_MATCH: 2,
    EXACT_MATCH: 3,
}

export default class PostProcessingRule {
    
    static MULTIINDEX_APPEND_CHAR = "-";  // used to separate multiple indexes in a single field
    
    constructor() {
        
        this.id = 0;
        this.name = "";
        this.type = -1;
        this.validation=null;
        this.caseValidation=null;
        this.caseExample=null;
        this.sampleValidation=null;
        this.sampleExample=null;
        this.caseIndex=0;
        this.sampleIndex=0;
    }
    
    get Type() {
        return this.type;
    }
    set Type(value) {
        this.type = value;
    }
    
    get Validation() {
        return this.validation;
    }
    set Validation(value) {
        this.validation = value;
    }

    FromJson(json) {
        this.id = json.id;
        this.name = json.name;
        this.type = json.type;
        this.validation = json.validation;
        this.caseValidation = json.caseValidation;
        this.caseExample = json.caseExample;
        this.sampleValidation = json.sampleValidation;
        this.sampleExample = json.sampleExample;
        this.caseIndex = json.caseIndex;
        this.sampleIndex = json.sampleIndex;
    }
    
    /**
     * attempt to run the post-processing rule against the given result.
     This throws LIValidationError if the rule fails OR if there is something wrong with the rule
     or the formatting of the incoming result.
     
     * @param barcode
     * @constructor
     */
    EvaluateBaseValidationAgainst(barcode:string) {
        if (this.type === PostProcessingRuleTypes.NONE ) { // always passes
            console.debug(">>>> just returning");
            return;
        }
        if (this.type === PostProcessingRuleTypes.EXACT_MATCH) {
            if (barcode === String(this.validation)) {
                console.debug(">>>> exact match found");
                return;
            } else {
                console.debug(">>>> exact match NOT found");
                throw new LIValidationError(`'${barcode}' is not equal '${this.validation}'`, this);
            }
        }

        if (this.type === PostProcessingRuleTypes.GREATER_THAN_MATCH) {
            try {
                const n = parseFloat(barcode);
                const validationAsNum = parseFloat(this.validation);
                if (n > validationAsNum) {
                    console.debug(">>>> greater than match found");
                    return;
                } else {
                    console.debug(">>>> greater than match NOT found");
                    throw new LIValidationError(`'${barcode}' is not > '${this.validation}'`, this);
                }
            } catch (ex) {
                if (ex instanceof LIValidationError) { // rethrow the inner exception
                    throw ex;
                }
                const msg = `failed to convert '${barcode}' or validation '${this.validation}' to float for > match test..failing test`;
                console.error(`PostProcessingRules: ${msg}`);
                throw new LIValidationError(msg, this);
            }
        }

        if (this.type === PostProcessingRuleTypes.LESS_THAN_MATCH) {
            try {
                const n = parseFloat(barcode);
                const validationAsNum = parseFloat(this.validation);
                if (n < validationAsNum) {
                    console.debug(">>>> less than match found");
                    return;
                } else {
                    console.debug(">>>> less than match NOT found");
                    throw new LIValidationError(`'${barcode}' is not < '${this.validation}'`, this);
                }
            } catch (ex) {
                if (ex instanceof LIValidationError) { // rethrow the inner exception
                    throw ex;
                }
                const msg = `failed to convert '${barcode}' or validation '${this.validation}' to float for < match test..failing test`;
                console.error(`PostProcessingRules: ${msg}`);
                throw new LIValidationError(msg, this);
            }
        }

        if (this.type === PostProcessingRuleTypes.TEXT_MATCH) {
            try {
                if (this.validation === null || !new RegExp(this.validation).test(barcode)) {
                    console.debug(">>>> regex text match NOT found");
                    throw new LIValidationError(`'${barcode}' does not match '${this.validation}'`, this);
                } else {
                    console.debug(">>>> regex text match found");
                    return;
                }
            } catch (ex) {
                if (ex instanceof LIValidationError) { // rethrow the inner exception
                    throw ex; // rethrow
                }
                console.error(`REGEXP error found while evaluating base validation.  Details: '${ex.message}'`);
                throw new LIValidationError(`Problem with RegExp in base validation for '${String(this)}.\n\nDetails: '${ex.message}'`, this);
            }
        }

        console.error(`unknown Post Processing Rule type '${this.type}' found...ignoring`);
    }

    /**
     * attempt to run the post-processing rule against the given result inspecting the
     result as a MAGAZINE barcode value -- this is special in order to keep the rules simpler.
     This throws LIValidationError if the rule fails OR if there is something wrong with the rule
     or the formatting of the incoming result.
     
     * @param barcode
     * @constructor
     */
    EvaluateAsMag(barcode:string) {
        if (!barcode) {
            barcode = "";
        }
        const valMain = "\\d{4}.[1-9][\\d]*"; // trailing piece
        if (!this.validation || this.validation === "") { // empty validation not allowed
            throw new LIValidationError("empty value passed in for magazine barcode validation", this);
        }
        const [valYear, valPneumonic] = PostProcessingRule.findYearAndPneumonic(this.validation);
        const matchStr=`^${valYear}${valPneumonic}.${valMain}`
        const re = RegExp(matchStr);
        if (re.test(barcode)) {
            console.debug("mag validation match found");
        }
        else {
            throw new LIValidationError(`'${barcode}' does not match '${matchStr}' given validation of '${this.validation}'`,
                                            this);
        }
    }

    /**
     * split up the year and the pneumonic from the validation (mags only).
     We have the following cases:
     1 - 20M, 20MK
     2 - 20
     3 - M   - *** not strictly allowed (we convert this to case 7)
     4 - 2*
     5 - 1*M
     6 - *
     7 - *M
     8 - *2M, *2MK

     * @param validationStr
     */
    static findYearAndPneumonic(validationStr: string) {
        let year = "";           // changed by pott - was current year prior to 24.0112
        let pneumonic = "";

        if (validationStr === "*") { // case 6 
            return ["\\d\\d", ""];   // any YY<0 or more something>
        }

        if (validationStr.length >= 2) {
            if (/\d/.test(validationStr[0]) && /\d/.test(validationStr[1])) {   // cases 1,2
                year = parseInt(validationStr.substring(0, 2));
                pneumonic = validationStr.substring(2);     // remainder  
            } else if (/\d/.test(validationStr[0]) && validationStr[1] === "*") {      // case 4, 5
                year = `${validationStr[0]}\\d`;
                pneumonic = validationStr.substring(2);     // remainder 
            } else if (validationStr[0] === "*") {              // added 231206 - pott
                if (!/\d/.test(validationStr[1])) {   // case 7
                    pneumonic = validationStr.substring(1);     // remainder
                    year = "\\d\\d";   // any YY
                } else {   // case 8
                    pneumonic = validationStr.substring(2);     // remainder
                    year = `\\d${validationStr[1]}`;   // any decade
                }
            }
        } else {  // case 3 (converted to case 7)
            year = "\\d\\d";   // any YY
            pneumonic = validationStr;         // whole thing is pneumonic regex                                 
        }

        return [year, pneumonic];
    }

    /**
     * set the case and sample numbers based on the setup of this PPR
     * The augmented slide is returned with the case and sample numbers set, if possible
     * 
     * @param slide
     * @returns {{SampleNumber}|{CaseNumber}|*}
     * @constructor
     */
    SetCaseAndSampleNumbersIfPossible(slide:SampleBasic):SampleBasic {
        // Reset case and sample numbers
        slide.caseNumber = '';
        slide.sampleNumber = '';

        console.info(`attempting case/sample number breakdowns. SlideBC='${slide.barcode}', ${this.name}`);
        const slideBC = slide.barcode;
        if (!slideBC) {
            console.debug(">>>> slide barcode is empty. post processing rule FAILS");
            throw new LIValidationError("Can't break down case #/sample #: empty slide barcode passed in", this, null);
        }

        if (this.Type !== PostProcessingRuleTypes.TEXT_MATCH) {
            console.debug(">>>> post processing rule is not type 'TEXT_MATCH'. Can't do breakdown (not a validation error)");
            return slide;
        }

        if (!this.Validation) {
            console.debug(">>>> base validation is empty. Can't do breakdown (validation error)");
            throw new LIValidationError("Can't break down case #/sample #: empty base validation rule", this);
        }

        if (this.caseIndex <= 0 && this.sampleIndex <= 0) {
            console.debug(">>>> both case and sample indexes are 0. WON'T do breakdown (not a validation error)");
            console.info("Empty case# and sample# indexes found so WON'T do breakdown (this is ok)");
            return slide;
        }

        const breakDown = new RegExp(this.Validation).exec(slideBC);
        if (!breakDown) {
            console.debug(">>>> base validation no longer matches slide barcode. Can't do breakdown (validation error)");
            throw new CaseBreakdownError(`Can't break down case #/sample #: barcode '${slideBC}' doesn't match '${this.Validation}' anymore?`, this, slideBC);
        }

        const breakDownGroups = breakDown.slice(1);
        if (breakDownGroups.length === 0 && (this.caseIndex > 0 || this.sampleIndex > 0)) { // we have indexes but no groups
            console.debug(">>>> no breakdown groups found in validation string. Can't do breakdown (validation error)");
            throw new CaseBreakdownError("Can't break down case #/sample #: no '()' groups found in validation portion of the rule?", this, slideBC);
        }

        // do the case# breakdown?
        if(this.caseIndex>0) {
            const caseIndexList = this.handleBasicIndexes(this.caseIndex);
            if (!caseIndexList) {
                throw new CaseBreakdownError(`Can't break down case #/sample #: too many indexes supplied for case index (found '${this.caseIndex}')?`, this, slideBC);
            }

            for (const caseIndex of caseIndexList) {
                if (caseIndex > 0) {
                    if (breakDownGroups.length >= caseIndex) {
                        const curMatch = breakDown[caseIndex];
                        slide.caseNumber = slide.caseNumber === '' ? curMatch : slide.caseNumber + PostProcessingRule.MULTIINDEX_APPEND_CHAR + curMatch;
                        console.debug(`set '${curMatch}' into CASE NUMBER '${slide.caseNumber}' based on barcode '${slideBC}'`);
                    } else {
                        throw new CaseBreakdownError(`Can't break down case #: matching value for index '${caseIndex}' not found`, this, slideBC);
                    }
                }
            }
            if (!slide.caseNumber) {
                console.debug(">>>> case number is not set after breakdown attempt (validation error)");
                throw new CaseBreakdownError(`full case/sample break down not successful for barcode '${slideBC}' (case#='${slide.caseNumber}',sample#='${slide.sampleNumber}')`,
                    this, slideBC, slide.caseNumber, slide.sampleNumber);
            }
        }

        // do the sample# breakdown?
        if(this.sampleIndex>0) {
            const sampleIndexList = this.handleBasicIndexes(this.sampleIndex);
            if (!sampleIndexList) {
                throw new CaseBreakdownError(`Can't break down case #/sample #: too many indexes supplied for sample index (found '${this.sampleIndex}')?`, this, slideBC, slide.caseNumber);
            }

            for (const sampleIndex of sampleIndexList) {
                if (sampleIndex > 0) {
                    if (breakDownGroups.length >= sampleIndex) {
                        const curMatch = breakDown[sampleIndex];
                        slide.sampleNumber = slide.sampleNumber === '' ? curMatch : slide.sampleNumber + PostProcessingRule.MULTIINDEX_APPEND_CHAR + curMatch;
                        console.debug(`sample '${curMatch}' to sample number '${slide.sampleNumber}' based on barcode '${slideBC}'`);
                    } else {
                        throw new CaseBreakdownError(`Can't break down sample #: matching value for index '${sampleIndex}' not found`, this, slideBC, slide.caseNumber);
                    }
                }
            }

            if (!slide.caseNumber || !slide.sampleNumber) {
                console.debug(">>>> slide number is not set after breakdown attempt (validation error)");
                throw new CaseBreakdownError(`full case/sample break down not successful for barcode '${slideBC}' (case#='${slide.caseNumber}',sample#='${slide.sampleNumber}')`, this, slideBC, slide.caseNumber, slide.sampleNumber);
            }
        }

        return slide;
    }

    /**
     * break up a indexList provided as 123 into 1, 2, and 3 indexes so that we can append them to form
     a complex case number.
     NOTE: this means you can't use indexes > 9, which should be a fairly safe limitation for the moment.
     
     * @param basicIndexValue
     * @returns {(number|number)[]|number[]|null|*[]}
     */
    handleBasicIndexes(basicIndexValue) {
        if (basicIndexValue <= 0) {
            return [0];
        } else if (basicIndexValue < 10) {
            return [basicIndexValue];
        } else if (basicIndexValue < 100) {
            const first = Math.floor(basicIndexValue / 10);
            const second = basicIndexValue - first * 10;
            return [first, second];
        } else if (basicIndexValue < 1000) {
            const first = Math.floor(basicIndexValue / 100);
            const second = Math.floor((basicIndexValue - first * 100) / 10);
            const third = basicIndexValue - first * 100 - second * 10;
            return [first, second, third];
        }
        return null;
    }
}